/**
 * Extra filter controls for the Notification Logs bar — a channel select
 * and a date field, wired to the ListActionsBar pending-filter context
 * and styled EXACTLY like FilterField (same 39px input, label above via
 * the Roomgrid theme, label registered for the applied-filter chips).
 */

import React from "react";
import { MenuItem, TextField } from "@mui/material";
import {
  FILTER_INPUT_HEIGHT,
  useFilterBarContext,
} from "@/components/common";

const CHANNELS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "telegram", label: "Telegram" },
  { value: "slack", label: "Slack" },
  { value: "teams", label: "Teams" },
  { value: "discord", label: "Discord" },
  { value: "googlechat", label: "Google Chat" },
  { value: "wecom", label: "WeCom" },
  { value: "mattermost", label: "Mattermost" },
  { value: "rocketchat", label: "Rocket.Chat" },
  { value: "viber", label: "Viber" },
  { value: "line", label: "LINE" },
  { value: "inbox", label: "Inbox" },
  { value: "webhook", label: "Webhook" },
  { value: "webpush", label: "Web Push" },
  { value: "apppush", label: "App Push" },
];

const INPUT_SX = {
  flexShrink: 0,
  "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
    height: FILTER_INPUT_HEIGHT,
    minHeight: FILTER_INPUT_HEIGHT,
  },
  "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiOutlinedInput-input": {
    py: 0,
  },
} as const;

export const ChannelFilterSelect: React.FC<{
  label: string;
  allLabel: string;
  sx?: object;
}> = ({ label, allLabel, sx }) => {
  const { pendingValues, setPendingValue, registerLabel } =
    useFilterBarContext();
  const value = (pendingValues.channel as string | undefined) ?? "";

  React.useEffect(() => {
    registerLabel("channel", label);
  }, [label, registerLabel]);

  return (
    <TextField
      select
      name="channel"
      label={label}
      value={value}
      onChange={(e) => setPendingValue("channel", e.target.value)}
      size="small"
      variant="outlined"
      InputLabelProps={{ shrink: true }}
      SelectProps={{ displayEmpty: true }}
      sx={{ width: { xs: "100%", sm: 160 }, ...INPUT_SX, ...sx }}
    >
      <MenuItem value="">{allLabel}</MenuItem>
      {CHANNELS.map((c) => (
        <MenuItem key={c.value} value={c.value}>
          {c.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

export const DateFilterField: React.FC<{
  source: string;
  label: string;
  sx?: object;
}> = ({ source, label, sx }) => {
  const { pendingValues, setPendingValue, registerLabel } =
    useFilterBarContext();
  const value = (pendingValues[source] as string | undefined) ?? "";

  React.useEffect(() => {
    registerLabel(source, label);
  }, [source, label, registerLabel]);

  return (
    <TextField
      type="date"
      name={source}
      label={label}
      value={value}
      onChange={(e) => setPendingValue(source, e.target.value)}
      size="small"
      variant="outlined"
      InputLabelProps={{ shrink: true }}
      sx={{ width: { xs: "100%", sm: 170 }, ...INPUT_SX, ...sx }}
    />
  );
};
